import { throttle } from "lodash-es";
import {
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import EntityNotFoundAlert from "@/common/components/AppAlerts/EntityNotFoundAlert";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import Chat, { ChatProps } from "@/common/components/Entity/Chat/Chat";
import { ChatDisplayHandle } from "@/common/components/Entity/Chat/ChatDisplay";
import { GeneralScopeHelper } from "@/common/helpers/generalScope";
import { useUnmountEffect } from "@/common/hooks/mount/useUnmountEffect";
import { useAppThunkDispatch } from "@/common/hooks/redux";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { ValidationHelper } from "@/common/validation";
import { AppPermission, ChatType } from "@/core/api/generated/v1";
import * as chatsSlice from "@/store/communication/chatsSlice";
import { OpenChatInfo } from "@/store/communication/chatsSlice";

interface OwnProps {
  chatId?: string | null;
  chatType?: ChatType | null;
  chatScopeStr?: string | null;
  chatProps?: Partial<ChatProps>;
}

type Props = OwnProps;

export default forwardRef(function EmbeddedChat(
  { chatId, chatType, chatScopeStr, chatProps }: Props,
  ref: Ref<ChatDisplayHandle>,
) {
  const { enqueueSnackbar } = useAppSnackbar();
  const thunkDispatch = useAppThunkDispatch();

  const [openedChat, setOpenedChat] = useState<OpenChatInfo | undefined>(undefined);

  const chatRef = useRef<ChatDisplayHandle>(null);
  useImperativeHandle(ref, () => chatRef.current as ChatDisplayHandle, [chatRef.current]);

  const chatScope = useMemo(
    () => GeneralScopeHelper.deserializeScopeFromString(chatScopeStr),
    [chatScopeStr],
  );

  const canShowChat = !!chatId || !!chatScope;

  const handleOpenChat = useCallback(async () => {
    if (canShowChat) {
      try {
        const newOpenedChat = await thunkDispatch(
          chatsSlice.openChat({
            placement: "page",
            chatType: chatType,
            chatId: chatId,
            scope: chatScope,
          }),
        );
        setOpenedChat(newOpenedChat);
        return newOpenedChat;
      } catch (err) {
        const newValidation = ValidationHelper.handleApiErrorResponse(err);
        enqueueSnackbar(newValidation.generalError, { variant: "error" });
      }
    }
  }, [chatType, chatId, chatScope]);

  const handleOpenChatThrottle = useCallback(
    throttle(handleOpenChat, 500, {
      leading: true,
      trailing: false,
    }),
    [handleOpenChat],
  );

  const handleCloseChat = useCallback(async () => {
    try {
      await thunkDispatch(
        chatsSlice.closeChat({
          openedChat,
          chat: undefined,
        }),
      );
    } catch (err) {
      const newValidation = ValidationHelper.handleApiErrorResponse(err);
      enqueueSnackbar(newValidation.generalError, { variant: "error" });
    }
  }, [openedChat]);

  const handleCloseChatThrottle = useCallback(
    throttle(handleCloseChat, 500, {
      leading: true,
      trailing: false,
    }),
    [],
  );

  useEffect(() => {
    handleOpenChatThrottle();
  }, []);

  // ensure closeChat is called only once on unmount
  useUnmountEffect(() => {
    if (openedChat) {
      handleCloseChatThrottle();
    }
  }, [openedChat]);

  if (!canShowChat) {
    return <EntityNotFoundAlert />;
  }

  return (
    <AuthorizedElement permissions={[AppPermission.ChatRead]} withAlert>
      <Chat
        ref={(r) => {
          chatRef.current = r;
        }}
        chatType={chatType!}
        chatId={chatId || undefined}
        scope={chatScope || undefined}
        allowClose={false}
        allowPin={false}
        allowLeave={chatProps?.allowLeave}
        allowAttachments={chatProps?.allowAttachments}
        fullWidth={chatProps?.fullWidth}
        fullHeight={chatProps?.fullHeight}
        disabled={chatProps?.disabled}
        maxHeight='100%'
        chatHistoryProps={{
          autoScrollToHistoryEnd: false,
        }}
        onClose={(chat) => {
          handleCloseChat();
        }}
        {...chatProps}
      />
    </AuthorizedElement>
  );
});
